import { apiUrl, getApiBaseUrl } from "#/utils/api";

/**
 * Sets up a fetch interceptor that automatically refreshes the session
 * when a 401 Unauthorized response is received.
 *
 * This is called at app startup to ensure all fetch requests are protected.
 */
export function installSessionRefreshFetchInterceptor() {
	const originalFetch = window.fetch;
	const apiBaseUrl = getApiBaseUrl();
	const apiRequestPrefix = `${apiBaseUrl}/v1/`;

	const isApiRequest = (input: RequestInfo | URL): boolean => {
		const resolvedUrl =
			typeof input === "string"
				? new URL(input, window.location.origin)
				: input instanceof URL
					? input
					: new URL(input.url, window.location.origin);

		return resolvedUrl.toString().startsWith(apiRequestPrefix);
	};

	const interceptedFetch = async (
		input: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> => {
		const requestInit = isApiRequest(input)
			? {
					...init,
					credentials: init?.credentials ?? "include",
				}
			: init;

		let response = await originalFetch(input, requestInit);

		// If we get a 401, attempt to refresh the session and retry
		if (response.status === 401) {
			try {
				// Attempt to refresh the session
				const refreshResponse = await originalFetch(apiUrl("/auth/refresh"), {
					method: "POST",
					credentials: "include",
				});

				if (refreshResponse.ok) {
					// Session was refreshed, retry the original request
					response = await originalFetch(input, requestInit);
				}
			} catch (error) {
				// Refresh failed, let the original 401 response go through
				console.warn("Session refresh failed:", error);
			}
		}

		return response;
	};

	window.fetch = Object.assign(interceptedFetch, originalFetch) as typeof window.fetch;
}
